// The process library: every process map (SOP) by area, with where each stands (released, or a draft in progress)
// and how many of its steps agents do. Each area's home shows its own processes the same way.
import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AREAS, areaName, nextRev, type TemplateInfo } from "@couragemfg/api/processes-model";
import { CommandBar } from "../Shell";
import { ErrorBox } from "../ui";
import { useLoad } from "../hooks";
import { can, useAccess } from "../security/store";
import { fmtDate, processes, type ProcessRow } from "./processApi";
import { useStartFromMap } from "./StartFromMap";
import "../maintenance/mx.css";
import "./processes.css";

function Standing({ r }: { r: ProcessRow }) {
  if (!r.rev) return <span className="pmap-standing"><span className="pmap-status warn">Not released</span><span className="muted">Draft</span></span>;
  return (
    <span className="pmap-standing"><span className="pmap-status ok">Rev {r.rev} in effect</span>
      {r.changes > 0 && <span className="muted">Rev {nextRev(r.rev)} draft: {r.changes} change{r.changes === 1 ? "" : "s"}</span>}</span>
  );
}
const stepsLine = (r: ProcessRow) => `${r.steps} steps${r.agentSteps ? `, ${r.agentSteps} by agents` : ""}`;

export function ProcessLibrary() {
  const [q, setQ] = useSearchParams();
  const area = q.get("area") ?? "";
  const access = useAccess();
  const canCreate = can(access, "process-maps", "create") && !access?.viewAs?.readOnly;
  const list = useLoad(() => processes.list(area || undefined), [area]);
  const rows = list.data;
  const [showNew, setShowNew] = useState(q.get("new") === "1");
  const released = rows?.filter((r) => r.rev).length ?? 0, drafts = rows?.filter((r) => !r.rev || r.changes > 0).length ?? 0;
  const agentSteps = rows?.reduce((a, r) => a + r.agentSteps, 0) ?? 0;
  const starter = useStartFromMap();
  return (
    <>
      <CommandBar status={rows ? `${rows.length} process map${rows.length === 1 ? "" : "s"}` : "Loading…"}>
        {canCreate && <button className="cmd primary" onClick={() => setShowNew(true)}>New process map</button>}
        <label className="pmap-filter"><span className="sr-only">Area</span>
          <select className="select" value={area} onChange={(e) => { const n = new URLSearchParams(q); if (e.target.value) n.set("area", e.target.value); else n.delete("area"); setQ(n); }}>
            <option value="">All areas</option>{AREAS.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select></label>
      </CommandBar>
      <div className="page">
        <div className="page-title"><h1>Process maps</h1><span className="sub">How each process runs, step by step and by role. Each map is its SOP. People and agents follow the revision in effect.</span></div>
        <ErrorBox error={list.error ?? starter.error} />
        {showNew && canCreate && <NewProcess area={area} onCancel={() => setShowNew(false)} />}
        <div className="mx-kpis">
          <div className="mx-kpi"><span className="l">Processes</span><span className="v">{rows?.length ?? "–"}</span></div>
          <div className="mx-kpi"><span className="l">Released</span><span className="v">{rows ? released : "–"}</span></div>
          <div className="mx-kpi"><span className="l">Drafts in progress</span><span className="v">{rows ? drafts : "–"}</span></div>
          <div className="mx-kpi"><span className="l">Steps agents do</span><span className="v">{rows ? agentSteps : "–"}</span></div>
        </div>
        <section className="card">
          {!rows ? <div className="empty">Loading…</div> : !rows.length ? (
            <div className="empty">{area ? "No process maps in this area yet." : "No process maps yet."} {canCreate && <button className="linkish" onClick={() => setShowNew(true)}>Map the first one</button>}</div>
          ) : (
            <div className="pmap-tscroll">
              <table className="pmap-table">
                <thead><tr><th>Document</th><th>Process</th><th>Area</th><th>Where it stands</th><th>Updated</th><th><span className="sr-only">Start</span></th></tr></thead>
                <tbody>{rows.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{r.docNo}</td>
                    <td><Link to={`/processes/${r.id}`}><b>{r.title}</b></Link><div className="muted">{stepsLine(r)} · {r.lanes} lanes · {r.scenarios} scenario{r.scenarios === 1 ? "" : "s"}{r.owner ? ` · ${r.owner}` : ""}</div></td>
                    <td>{r.areaName || "–"}</td>
                    <td><Standing r={r} /></td>
                    <td className="muted">{fmtDate(r.updatedAt)}{r.updatedByName ? ` · ${r.updatedByName}` : ""}</td>
                    <td className="pmap-row-acts"><button className="btn pmap-sm" disabled={starter.busy} onClick={() => starter.start(r.id, "issue")}>Raise an issue</button>
                      <button className="btn pmap-sm" disabled={starter.busy} onClick={() => starter.start(r.id, "improve")}>Improve</button></td>
                  </tr>))}</tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

const BLANK: TemplateInfo = { id: "blank", name: "Blank map", area: "", summary: "One lane, a start, one step and an end." };

function NewProcess({ area, onCancel }: { area: string; onCancel: () => void }) {
  const nav = useNavigate();
  const t = useLoad(() => processes.templates(), []);
  const list = t.data?.templates ?? [BLANK];
  const [f, setF] = useState({ title: "", area, owner: "", docNo: "", template: "blank", summary: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  // Picking a template fills in its name, area and owner, unless they were typed: a value that came from the
  // previous template is replaced, anything else stays.
  const pick = (x: TemplateInfo) => setF((cur) => {
    const was = list.find((y) => y.id === cur.template);
    const mine = (v: string, from?: string) => !!v.trim() && v !== from;
    return {
      ...cur, template: x.id,
      title: mine(cur.title, was && was.id !== "blank" ? was.name : undefined) ? cur.title : x.id === "blank" ? "" : x.name,
      owner: mine(cur.owner, was?.owner) ? cur.owner : x.owner ?? "",
      area: mine(cur.area, was?.area) ? cur.area : x.area || cur.area,
    };
  });
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try { const m = await processes.create({ ...f, area: f.area || null, docNo: f.docNo.trim() || undefined }); nav(`/processes/${m.id}`, { state: { fresh: true } }); }
    catch (x) { setErr(x); setBusy(false); }
  };
  return (
    <form className="card pmap-new" onSubmit={submit}>
      <div className="card-head"><h2>New process map</h2></div>
      <div className="card-body">
        <ErrorBox error={err ?? t.error} />
        <TemplatePicker list={list} value={f.template} area={area} onPick={pick} />
        <div className="form-grid">
          <div className="field"><label htmlFor="pmap-t">Process</label><input id="pmap-t" className="input" required maxLength={120} placeholder="e.g. Order to ship" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
          <div className="field"><label htmlFor="pmap-a">Area</label><select id="pmap-a" className="select" value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })}><option value="">No area</option>{AREAS.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
          <div className="field"><label htmlFor="pmap-o">Process owner</label><input id="pmap-o" className="input" maxLength={80} placeholder="A role, e.g. Supply chain manager" value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} /></div>
          <div className="field"><label htmlFor="pmap-n">Document number</label><input id="pmap-n" className="input mono" maxLength={40} placeholder="Next in the area's series" value={f.docNo} onChange={(e) => setF({ ...f, docNo: e.target.value.toUpperCase() })} /><span className="hint">Leave blank for the next number, like SOP-SC-001.</span></div>
          <div className="field full"><label htmlFor="pmap-s">Purpose</label><input id="pmap-s" className="input" maxLength={500} placeholder="What this process does and when it applies" value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} /></div>
        </div>
        <div className="btn-row"><button className="btn primary" disabled={busy || !f.title.trim()}>{busy ? "Creating…" : "Create and open the map"}</button><button type="button" className="btn" onClick={onCancel}>Cancel</button></div>
      </div>
    </form>
  );
}

/** The starting points, by area, with a search: a blank map first, then every template. */
function TemplatePicker({ list, value, area, onPick }: { list: TemplateInfo[]; value: string; area: string; onPick: (t: TemplateInfo) => void }) {
  const [q, setQ] = useState("");
  const [only, setOnly] = useState(area); // opens on the area the map is for; then it's the person's filter
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const areas = useMemo(() => AREAS.filter((a) => list.some((x) => x.area === a.id)), [list]);
  const hits = list.filter((x) => x.id !== "blank" && (!only || x.area === only)
    && words.every((w) => `${x.name} ${x.summary} ${x.owner ?? ""} ${areaName(x.area)}`.toLowerCase().includes(w)));
  const groups = areas.map((a) => ({ a, items: hits.filter((x) => x.area === a.id) })).filter((g) => g.items.length);
  const blank = list.find((x) => x.id === "blank") ?? BLANK;
  const option = (x: TemplateInfo) => (
    <label key={x.id} className={`pmap-tpl-opt ${value === x.id ? "on" : ""}`}>
      <input type="radio" name="tpl" value={x.id} checked={value === x.id} onChange={() => onPick(x)} />
      <span><b>{x.name}</b><span className="muted">{x.summary}</span>
        {x.steps !== undefined && <span className="meta">{x.steps} steps · {x.lanes} lane{x.lanes === 1 ? "" : "s"}{x.scenarios ? ` · ${x.scenarios} scenario${x.scenarios === 1 ? "" : "s"}` : ""}{x.agent && <span className="pmap-agent-tag">Agent lane</span>}</span>}
      </span>
    </label>
  );
  return (
    <fieldset className="pmap-tpl">
      <legend>Start from</legend>
      <div className="pmap-tpl-bar">
        <input className="input" type="search" aria-label="Search templates" placeholder="Search, e.g. MRB, cycle count, month-end" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="select" aria-label="Templates for" value={only} onChange={(e) => setOnly(e.target.value)}>
          <option value="">All areas</option>{areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <span className="muted">{hits.length} template{hits.length === 1 ? "" : "s"}</span>
      </div>
      <div className="pmap-tpl-list">
        <div className="pmap-tpl-grid">{option(blank)}</div>
        {groups.map((g) => <div key={g.a.id} className="pmap-tpl-group"><h3>{g.a.name}</h3><div className="pmap-tpl-grid">{g.items.map(option)}</div></div>)}
        {!hits.length && <p className="muted" style={{ margin: "10px 2px 2px" }}>{only && !words.length ? `No templates for ${areaName(only)} yet. Start from a blank map, or look in all areas.` : "No template matches. Try another word, or start from a blank map."}</p>}
      </div>
    </fieldset>
  );
}

/** An area's processes, on its home page. */
export function AreaProcesses({ area, name }: { area: string; name: string }) {
  const { data } = useLoad(() => processes.list(area), [area]);
  const access = useAccess();
  const canCreate = can(access, "process-maps", "create") && !access?.viewAs?.readOnly;
  return (
    <section aria-labelledby="pmap-area-h" className="pmap-area-sec">
      <div className="sec-h">
        <h2 id="pmap-area-h" style={{ margin: 0, fontSize: 16 }}>Processes</h2>
        <span className="muted">How {name} works: process maps and SOPs, for people and agents</span>
        <span className="pmap-area-acts"><Link to={`/processes?area=${area}`}>All {name} processes</Link>{canCreate && <Link to={`/processes?area=${area}&new=1`}>New process map</Link>}</span>
      </div>
      {!data ? null : !data.length ? <p className="muted" style={{ margin: 0 }}>No process maps here yet.{canCreate ? " Map how the work runs, then release it as the SOP." : ""}</p> : (
        <div className="pmap-cards">{data.map((r) => (
          <Link key={r.id} className="pmap-card" to={`/processes/${r.id}`}>
            <span className="no mono">{r.docNo}</span>
            <span className="ti">{r.title}</span>
            <Standing r={r} />
            <span className="muted">{stepsLine(r)} · {r.scenarios} scenario{r.scenarios === 1 ? "" : "s"}</span>
          </Link>))}</div>
      )}
    </section>
  );
}
